import { emptyApi } from "./emptyApi";
import * as CHAT_SETTING_BANNERS from "constants/chatSettingBannerTypes";

export type CHAT_SETTING_BANNER_TYPES =
  (typeof CHAT_SETTING_BANNERS)[keyof typeof CHAT_SETTING_BANNERS];

export type ChatSettingsResponse = {
  banners: Banner[];
  holidays: Holiday[];
};

export type Banner = {
  uuid: string;
  banner_text: string;
  banner_type: CHAT_SETTING_BANNER_TYPES;
  expiry_date?: string;
  is_active?: boolean;
};

export type Holiday = {
  uuid: string;
  date: string;
  timezone: string;
};

export const chatSettingsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getChatSettings: build.query<ChatSettingsResponse, void>({
      query: () => "/api/inkle/messaging/chat-settings/",
      providesTags: ["CHAT_SETTINGS"],
    }),

    editBanner: build.mutation<
      {
        uuid: string;
        banner_text: string;
        banner_type: string;
      },
      {
        uuid: string;
        text: string;
        expiry_date?: string;
      }
    >({
      query: ({ uuid, text, expiry_date }) => {
        return {
          url: `/api/inkle/messaging/chat-settings/banner/${uuid}/`,
          body: {
            text,
            expiry_date,
          },
          method: "PUT",
        };
      },
      invalidatesTags: ["CHAT_SETTINGS"],
    }),

    addHoliday: build.mutation<
      {
        uuid: string;
        date: string;
        timezone: string;
      },
      {
        date: string;
        timezone: string;
      }
    >({
      query: ({ date, timezone }) => ({
        url: "/api/inkle/messaging/chat-settings/holiday/",
        body: {
          date,
          timezone,
        },
        method: "POST",
      }),
      invalidatesTags: ["CHAT_SETTINGS"],
    }),

    getCurrentBanner: build.query<
      {
        uuid: string;
        banner_text: string;
        banner_type: string;
      },
      { groupId: string }
    >({
      query: ({ groupId }) =>
        `/api/inkle/messaging/group/${groupId}/sla-response/`,
      providesTags: ["CHAT_SETTINGS"],
    }),
    deleteBanner: build.mutation<
      ChatSettingsResponse,
      {
        banner_id: string;
      }
    >({
      query: ({ banner_id }) => ({
        url: `/api/inkle/messaging/chat-settings/banner/${banner_id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["CHAT_SETTINGS"],
    }),
    createBanner: build.mutation<
      ChatSettingsResponse,
      {
        banner_text: string;
        banner_type?: string;
        expiry_date?: string;
      }
    >({
      query: ({ banner_text, banner_type, expiry_date }) => ({
        url: "/api/inkle/messaging/chat-settings/",
        body: {
          banner_text,
          banner_type,
          expiry_date,
        },
        method: "POST",
      }),
      invalidatesTags: ["CHAT_SETTINGS"],
    }),
  }),
});

export const {
  useGetChatSettingsQuery,
  useEditBannerMutation,
  useAddHolidayMutation,
  useGetCurrentBannerQuery,
  useCreateBannerMutation,
  useDeleteBannerMutation,
} = chatSettingsApi;
